/**
 * Background options configuration for the image editor
 * Handles dynamic import of background images and provides color palette
 */

/**
 * Dynamically imports all files from a webpack require.context
 * @param {Function} requireContext - Webpack require.context function
 * @returns {Object} Object with filename as key and imported module as value
 */
const importAll = (requireContext) => {
  const images = {};
  
  try {
    requireContext.keys().forEach((item) => {
      const key = item.replace('./', '');
      images[key] = requireContext(item);
    });
  } catch (error) {
    console.error('Error importing images:', error);
  }
  
  return images;
};

/**
 * Sorts image files by their numeric identifier (e.g., bg_1, bg_2, bg_10)
 * @param {Object} images - Object with image filenames as keys
 * @returns {Array} Sorted array of image modules
 */
const sortImagesByNumber = (images) => {
  try {
    return Object.keys(images)
      .sort((a, b) => {
        // Extract numeric part from filename (e.g., "bg_12.jpg" -> 12)
        const getNumber = (filename) => {
          const match = filename.match(/bg_(\d+)/);
          return match ? parseInt(match[1], 10) : 0;
        };
        
        return getNumber(a) - getNumber(b);
      })
      .map(key => images[key]);
  } catch (error) {
    console.error('Error sorting images:', error);
    // Return unsorted array as fallback
    return Object.values(images);
  }
};

/**
 * Import background images from assets directory
 */
const backgroundImages = importAll(
  require.context('../assets/backgrounds/imgs', false, /\.(png|jpe?g|svg)$/i)
);

/**
 * Import thumbnail images from assets directory
 */
const thumbnailImages = importAll(
  require.context('../assets/backgrounds/thmbs', false, /\.(png|jpe?g|svg)$/i)
);

/**
 * Sorted arrays of background and thumbnail images
 */
const BG_IMAGES = sortImagesByNumber(backgroundImages);
const THUMBNAIL_IMAGES = sortImagesByNumber(thumbnailImages);

/**
 * Color palette organized by categories
 */
const COLOR_CATEGORIES = {
  neutral: [
    "#FFFFFF", // Pure White
    "#F8F9FA", // Off White
    "#E9ECEF", // Light Gray
    "#DEE2E6", // Silver
    "#CED4DA", // Gray
    "#ADB5BD", // Medium Gray
    "#6C757D", // Dark Gray
    "#343A40", // Charcoal
  ],
  
  warm: [
    "#FFF3E0", // Warm White
    "#FFE0B2", // Light Peach
    "#FFCCBC", // Soft Coral
    "#FFAB91", // Salmon
    "#FF8A65", // Deep Salmon
    "#FF7043", // Burnt Orange
    "#FF5722", // Deep Orange
  ],
  
  cool: [
    "#E3F2FD", // Ice Blue
    "#BBDEFB", // Light Blue
    "#90CAF9", // Sky Blue
    "#64B5F6", // Medium Blue
    "#42A5F5", // Blue
    "#2196F3", // Primary Blue
    "#1E88E5", // Deep Blue
  ],
  
  accent: [
    "#E8F5E9", // Mint
    "#C8E6C9", // Light Green
    "#A5D6A7", // Sage
    "#81C784", // Medium Green
    "#66BB6A", // Green
    "#4CAF50", // Forest Green
    "#43A047", // Deep Green
  ],
  
  pastel: [
    "#FCE4EC", // Light Pink
    "#F8BBD0", // Pink
    "#F48FB1", // Medium Pink
    "#F06292", // Deep Pink
    "#EC407A", // Rose
  ],
  
  earth: [
    "#EFEBE9", // Beige
    "#D7CCC8", // Light Brown
    "#BCAAA4", // Taupe
    "#A1887F", // Brown
    "#8D6E63", // Deep Brown
  ]
};

/**
 * Flattened array of all color options for backward compatibility
 */
const COLOR_OPTIONS = Object.values(COLOR_CATEGORIES).flat();

/**
 * Utility functions for working with background options
 */
const BGUtils = {
  /**
   * Get a random background image
   * @returns {string} Random background image URL
   */
  getRandomBackground: () => {
    if (BG_IMAGES.length === 0) return null;
    const randomIndex = Math.floor(Math.random() * BG_IMAGES.length);
    return BG_IMAGES[randomIndex];
  },

  /**
   * Get a random color from a specific category
   * @param {string} category - Color category name
   * @returns {string} Random color hex code
   */
  getRandomColorFromCategory: (category = 'neutral') => {
    const colors = COLOR_CATEGORIES[category] || COLOR_CATEGORIES.neutral;
    const randomIndex = Math.floor(Math.random() * colors.length);
    return colors[randomIndex];
  },

  /**
   * Get all available color categories
   * @returns {Array} Array of category names
   */
  getColorCategories: () => Object.keys(COLOR_CATEGORIES),

  /**
   * Validate if a color is in the predefined palette
   * @param {string} color - Hex color code
   * @returns {boolean} True if color exists in palette
   */
  isValidColor: (color) => COLOR_OPTIONS.includes(color.toUpperCase()),
};

/**
 * Configuration object for background options
 */
const BackgroundConfig = {
  // Image resources
  images: {
    backgrounds: BG_IMAGES,
    thumbnails: THUMBNAIL_IMAGES,
    count: BG_IMAGES.length,
  },
  
  // Color resources
  colors: {
    all: COLOR_OPTIONS,
    categories: COLOR_CATEGORIES,
    count: COLOR_OPTIONS.length,
  },
  
  // Utility functions
  utils: BGUtils,
  
  // Backward compatibility exports
  BG_IMAGES,
  THUMBNAIL_IMAGES,
  COLOR_OPTIONS,
  COLOR_CATEGORIES,
};

export default BackgroundConfig;

// Named exports for specific use cases
export {
  BG_IMAGES,
  THUMBNAIL_IMAGES,
  COLOR_OPTIONS,
  COLOR_CATEGORIES,
  BGUtils,
};